// # Product Analysis Calculator
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import { nationalityData } from '../../../../../../utils/nationality_data';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Area,
  Scatter,
  ScatterChart,
  ZAxis,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { 
  FaChartPie, 
  FaChartBar,
  FaFilter,
  FaMoneyBillWave,
  FaPercentage,
  FaDollarSign,
  FaShoppingCart,
  FaUsers,
  FaGlobe,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import './product_analysis.css';

// # Product type configurations
const PRODUCT_TYPES = {
  step1_library: {
    label: 'Step 1 Library Access',
    category: 'library'
  },
  step2_library: {
    label: 'Step 2 Library Access',
    category: 'library'
  },
  step1_mentorship: {
    label: 'Step 1 Mentorship',
    category: 'mentorship'
  },
  step2_mentorship: {
    label: 'Step 2 Mentorship',
    category: 'mentorship'
  },
  step1_premium: {
    label: 'Step 1 Premium',
    category: 'premium'
  },
  step2_premium: {
    label: 'Step 2 Premium',
    category: 'premium'
  },
  step1_mentorship_premium: {
    label: 'Step 1 Mentorship Premium',
    category: 'premium'
  },
  step2_mentorship_premium: {
    label: 'Step 2 Mentorship Premium',
    category: 'premium'
  }
};

const ProductAnalysis = ({ dateRange, onDataProcessed }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productData, setProductData] = useState({
    totalRevenue: 0,
    totalProducts: 0,
    totalTransactions: 0,
    revenueByProduct: [],
    revenueOverTime: [],
    productsByRegion: [],
    productsByCountry: [],
    productPerformance: [],
    topProducts: [],
    growthRate: 0,
    conversionRate: 0
  });
  
  // # Add state for product filtering
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [geographyView, setGeographyView] = useState('region'); // 'region' or 'country'
  
  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  // # Fetch and process product data
  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Extract all payment records
        let allPayments = [];
        
        // # Process each document
        financialSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions
              if (paymentId.startsWith('payment_') && paymentData.transaction_type === 'payment') {
                // # Extract payment date from metadata
                const paymentDate = new Date(paymentData.metadata?.created_at || '');
                
                // # Apply date range filter if provided
                if (dateRange) {
                  const startDate = new Date(dateRange.startDate);
                  const endDate = new Date(dateRange.endDate);
                  
                  // # Set hours for proper date comparison
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(23, 59, 59, 999);
                  
                  // # Skip if outside date range
                  if (paymentDate < startDate || paymentDate > endDate) {
                    return;
                  }
                }
                
                // # Add payment to collection with all necessary fields - directly use stored values
                allPayments.push({
                  id: paymentId,
                  doc_id: docId,
                  timestamp: paymentDate,
                  egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                  egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                  product: paymentData.pay?.product || 'unknown',
                  duration: paymentData.pay?.duration || '',
                  nationality: paymentData.user?.nationality || 'Unknown',
                  email: paymentData.user?.email || '',
                  name: paymentData.user?.name || '',
                  userId: paymentData.user?.userId || '',
                  currency: paymentData.pay?.currency || '',
                  currency_symbol: paymentData.pay?.currency_symbol || '',
                  budget_dvd_amount: paymentData.budget_dvd?.egp_amount || 0,
                  budget_dvd_percent: paymentData.budget_dvd?.percentage_of_total || 0,
                  // # Directly use the shares data without recalculation
                  shares: paymentData.shares || {}
                });
              }
            });
          }
        });
        
        // # Process product data with the payments
        processProductData(allPayments);
        
        // # Call the onDataProcessed callback if provided
        if (onDataProcessed) {
          onDataProcessed(allPayments);
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        setError('Failed to load product data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [dateRange, onDataProcessed]);
  
  // # Process product data for charts - simplified to use pre-calculated values
  const processProductData = (payments) => {
    // # Initialize data structures
    const productData = {
      totalRevenue: 0,
      productRevenue: {},
      productCount: {},
      productPercentage: {},
      productGrowth: {},
      productByNationality: {},
      productByRegion: {},
      productOverTime: {},
      productDuration: {},
      productConversion: {},
      productDetails: []
    };
    
    // # Calculate total revenue - directly use the pre-calculated values
    productData.totalRevenue = payments.reduce((sum, payment) => sum + payment.egp_total, 0);
    
    // # Calculate revenue by product
    const productRevenue = {};
    const productCategories = {};
    const productTransactions = {};
    const mentorRevenue = {}; // # Track revenue by mentor
    
    payments.forEach(payment => {
      const product = payment.product || 'Unknown';
      
      // # Determine product category
      let category = 'Other';
      for (const [key, value] of Object.entries(PRODUCT_TYPES)) {
        if (product.includes(key)) {
          category = value.category;
          break;
        }
      }
      
      // # Initialize product data if not exists
      if (!productRevenue[product]) {
        productRevenue[product] = {
          product,
          category,
          revenue: 0,
          transactions: 0,
          customers: new Set(),
          countries: {},
          regions: {},
          mentors: {} // # Track mentors for this product
        };
      }
      
      // # Update product data
      productRevenue[product].revenue += payment.egp_total;
      productRevenue[product].transactions++;
      
      if (payment.email) {
        productRevenue[product].customers.add(payment.email);
      }
      
      // # Track mentor data for mentorship products
      if (category === 'mentorship' || category === 'premium') {
        const mentorName = payment.mentor || 'Unknown Mentor';
        
        if (!productRevenue[product].mentors[mentorName]) {
          productRevenue[product].mentors[mentorName] = {
            revenue: 0,
            transactions: 0,
            customers: new Set()
          };
        }
        
        productRevenue[product].mentors[mentorName].revenue += payment.egp_total;
        productRevenue[product].mentors[mentorName].transactions++;
        
        if (payment.email) {
          productRevenue[product].mentors[mentorName].customers.add(payment.email);
        }
        
        // # Track overall mentor performance
        if (!mentorRevenue[mentorName]) {
          mentorRevenue[mentorName] = {
            mentor: mentorName,
            revenue: 0,
            transactions: 0,
            customers: new Set(),
            products: new Set()
          };
        }
        
        mentorRevenue[mentorName].revenue += payment.egp_total;
        mentorRevenue[mentorName].transactions++;
        mentorRevenue[mentorName].products.add(product);
        
        if (payment.email) {
          mentorRevenue[mentorName].customers.add(payment.email);
        }
      }
      
      // # Update country and region data
      const nationality = payment.nationality || 'Unknown';
      const region = getRegionFromNationality(nationality);
      
      if (!productRevenue[product].countries[nationality]) {
        productRevenue[product].countries[nationality] = {
          revenue: 0,
          transactions: 0
        };
      }
      
      if (!productRevenue[product].regions[region]) {
        productRevenue[product].regions[region] = {
          revenue: 0,
          transactions: 0
        };
      }
      
      productRevenue[product].countries[nationality].revenue += payment.egp_total;
      productRevenue[product].countries[nationality].transactions++;
      
      productRevenue[product].regions[region].revenue += payment.egp_total;
      productRevenue[product].regions[region].transactions++;
      
      // # Update category data
      if (!productCategories[category]) {
        productCategories[category] = {
          category,
          revenue: 0,
          transactions: 0,
          products: new Set()
        };
      }
      
      productCategories[category].revenue += payment.egp_total;
      productCategories[category].transactions++;
      productCategories[category].products.add(product);
    });
    
    // # Format revenue by product for charts
    const revenueByProduct = Object.values(productRevenue).map(data => ({
      product: data.product,
      category: data.category,
      revenue: data.revenue,
      transactions: data.transactions,
      customers: data.customers.size,
      averageRevenue: data.customers.size > 0 ? data.revenue / data.customers.size : 0,
      percentage: (data.revenue / productData.totalRevenue) * 100,
      mentors: Object.entries(data.mentors || {}).map(([name, stats]) => ({
        name,
        revenue: stats.revenue,
        transactions: stats.transactions,
        customers: stats.customers.size,
        percentage: (stats.revenue / data.revenue) * 100
      })).sort((a, b) => b.revenue - a.revenue)
    }));
    
    // # Format mentor revenue data
    const mentorPerformance = Object.values(mentorRevenue).map(data => ({
      mentor: data.mentor,
      revenue: data.revenue,
      transactions: data.transactions,
      customers: data.customers.size,
      products: data.products.size,
      percentage: (data.revenue / productData.totalRevenue) * 100,
      averagePerCustomer: data.customers.size > 0 ? data.revenue / data.customers.size : 0
    })).sort((a, b) => b.revenue - a.revenue);
    
    // # Format revenue by category for charts
    const revenueByCategory = Object.values(productCategories).map(data => ({
      category: data.category,
      revenue: data.revenue,
      transactions: data.transactions,
      products: data.products.size,
      percentage: (data.revenue / productData.totalRevenue) * 100
    }));
    
    // # Calculate revenue over time (monthly)
    const monthlyRevenue = {};
    payments.forEach(payment => {
      const date = payment.timestamp;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyRevenue[monthKey]) {
        monthlyRevenue[monthKey] = {
          month: monthKey,
          revenue: 0,
          transactions: 0,
          products: new Set()
        };
      }
      
      monthlyRevenue[monthKey].revenue += payment.egp_total;
      monthlyRevenue[monthKey].transactions++;
      monthlyRevenue[monthKey].products.add(payment.product);
    });
    
    // # Format revenue over time for charts
    const revenueOverTime = Object.entries(monthlyRevenue).map(([month, data]) => ({
      month,
      revenue: data.revenue,
      transactions: data.transactions,
      uniqueProducts: data.products.size
    })).sort((a, b) => a.month.localeCompare(b.month));
    
    // # Calculate products by region
    const regionData = {};
    const countryData = {};
    
    payments.forEach(payment => {
      const nationality = payment.nationality || 'Unknown';
      const region = getRegionFromNationality(nationality);
      const product = payment.product || 'Unknown';
      
      // # Update region data
      if (!regionData[region]) {
        regionData[region] = {
          region,
          revenue: 0,
          transactions: 0,
          products: new Set(),
          customers: new Set()
        };
      }
      
      regionData[region].revenue += payment.egp_total;
      regionData[region].transactions++;
      regionData[region].products.add(product);
      
      if (payment.email) {
        regionData[region].customers.add(payment.email);
      }
      
      // # Update country data
      if (!countryData[nationality]) {
        countryData[nationality] = {
          country: nationality,
          region,
          revenue: 0,
          transactions: 0,
          products: new Set(),
          customers: new Set()
        };
      }
      
      countryData[nationality].revenue += payment.egp_total;
      countryData[nationality].transactions++;
      countryData[nationality].products.add(product);
      
      if (payment.email) {
        countryData[nationality].customers.add(payment.email);
      }
    });
    
    // # Format region data for charts
    const productsByRegion = Object.values(regionData).map(data => ({
      region: data.region,
      revenue: data.revenue,
      transactions: data.transactions,
      uniqueProducts: data.products.size,
      customers: data.customers.size,
      percentage: (data.revenue / productData.totalRevenue) * 100
    }));
    
    // # Format country data for charts
    const productsByCountry = Object.values(countryData).map(data => ({
      country: data.country,
      region: data.region,
      revenue: data.revenue,
      transactions: data.transactions,
      uniqueProducts: data.products.size,
      customers: data.customers.size,
      percentage: (data.revenue / productData.totalRevenue) * 100
    }));
    
    // # Calculate product performance metrics
    const productPerformance = revenueByProduct.map(product => ({
      name: product.product,
      revenue: (product.revenue / productData.totalRevenue) * 100,
      transactions: (product.transactions / payments.length) * 100,
      customers: product.customers,
      averageValue: product.averageRevenue
    }));
    
    // # Calculate top products
    const topProducts = [...revenueByProduct]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
    
    // # Calculate growth rate (if we have time-series data)
    let growthRate = 0;
    if (revenueOverTime.length > 1) {
      const firstMonth = revenueOverTime[0].revenue;
      const lastMonth = revenueOverTime[revenueOverTime.length - 1].revenue;
      
      if (firstMonth > 0) {
        growthRate = ((lastMonth - firstMonth) / firstMonth) * 100;
      }
    }
    
    // # Calculate total products and transactions
    const totalProducts = new Set(payments.map(p => p.product)).size;
    const totalTransactions = payments.length;
    
    // # Set the processed data
    setProductData({
      totalRevenue: productData.totalRevenue,
      totalProducts: totalProducts,
      totalTransactions: totalTransactions,
      revenueByProduct: revenueByProduct,
      revenueByCategory: revenueByCategory,
      revenueOverTime: revenueOverTime,
      productsByRegion: productsByRegion,
      productsByCountry: productsByCountry,
      productPerformance: productPerformance,
      topProducts: topProducts,
      growthRate: growthRate,
      conversionRate: 0 // Placeholder - would need more data
    });
  };
  
  // # Helper function to determine region from nationality
  const getRegionFromNationality = (nationality) => {
    // # Handle empty or undefined nationality
    if (!nationality || nationality === 'Unknown') {
      return 'Unknown';
    }
    
    // # Normalize the nationality string
    const normalizedNationality = nationality.toLowerCase().trim();
    
    // # Check if it's in the nationalityData
    for (const [key, data] of Object.entries(nationalityData)) {
      if (data.name.toLowerCase() === normalizedNationality || 
          data.searchKeys.some(k => normalizedNationality.includes(k))) {
        // # Get region from searchKeys
        if (data.searchKeys.includes('arab')) return 'Arab';
        if (data.searchKeys.includes('asia')) return 'Asia';
        if (data.searchKeys.includes('europe')) return 'Europe';
        if (data.searchKeys.includes('americas')) return 'Americas';
        if (data.searchKeys.includes('africa')) return 'Africa';
        if (data.searchKeys.includes('oceania')) return 'Oceania';
      }
    }
    
    return 'Unknown';
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${value.toLocaleString()}`;
  };
  
  // # Format percentage values
  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };
  
  // # Format month labels
  const formatMonth = (monthKey) => {
    if (!monthKey || typeof monthKey !== 'string') return '';
    
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };
  
  // # Handle product selection change
  const handleProductChange = (e) => {
    setSelectedProduct(e.target.value);
  };
  
  // # Handle category selection change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };
  
  // # Filter products based on selection
  const getFilteredProducts = () => {
    let filtered = [...productData.revenueByProduct];
    
    if (selectedProduct !== 'all') {
      filtered = filtered.filter(item => item.product === selectedProduct);
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    return filtered;
  };
  
  // # Get unique categories from products
  const getUniqueCategories = () => {
    const categories = new Set(productData.revenueByProduct.map(item => item.category));
    return Array.from(categories);
  };

  return (
    <div className="product_analysis_container">
      {error && (
        <div className="product_analysis_error">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="product_analysis_loading">
          Loading product analysis data...
        </div>
      ) : (
        <>
          {/* Product Overview */}
          <div className="product_analysis_overview">
            <div className="product_analysis_stats-grid">
              <div className="product_analysis_stat-card">
                <FaMoneyBillWave className="product_analysis_stat-icon" />
                <h3>Total Revenue</h3>
                <p className="product_analysis_stat-value">
                  {formatCurrency(productData.totalRevenue)}
                </p>
              </div>
              <div className="product_analysis_stat-card">
                <FaShoppingCart className="product_analysis_stat-icon" />
                <h3>Total Products Sold</h3>
                <p className="product_analysis_stat-value">
                  {productData.totalProducts}
                </p>
              </div>
              <div className="product_analysis_stat-card">
                <FaUsers className="product_analysis_stat-icon" />
                <h3>Total Transactions</h3>
                <p className="product_analysis_stat-value">
                  {productData.totalTransactions}
                </p>
              </div>
              <div className="product_analysis_stat-card">
                <FaPercentage className="product_analysis_stat-icon" />
                <h3>Growth Rate</h3>
                <p className="product_analysis_stat-value">
                  {formatPercentage(productData.growthRate)}
                </p>
              </div>
            </div>
          </div>
          
          {/* Product Filter Controls */}
          <div className="product_analysis_filter-controls">
            <div className="product_analysis_filter-group">
              <label>Product:</label>
              <select 
                className="product_analysis_filter-select"
                value={selectedProduct}
                onChange={handleProductChange}
              >
                <option value="all">All Products</option>
                {productData.revenueByProduct.map((product, index) => (
                  <option key={index} value={product.product}>
                    {product.product}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="product_analysis_filter-group">
              <label>Category:</label>
              <select 
                className="product_analysis_filter-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="all">All Categories</option>
                {getUniqueCategories().map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Top Products */}
          <div className="product_analysis_chart-container">
            <h3>Top 5 Products by Revenue</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={productData.topProducts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="product" />
                <YAxis />
                <Tooltip formatter={(value, name) => {
                  if (name === 'revenue') return formatCurrency(value);
                  return value;
                }} />
                <Legend />
                <Bar dataKey="revenue" fill="#8884d8" name="Revenue (EGP)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Revenue by Category */}
          <div className="product_analysis_chart-container">
            <h3>Revenue by Category</h3>
            <div className="product_analysis_chart-grid">
              <div className="product_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={productData.revenueByCategory}
                      dataKey="revenue"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ category, percentage }) => `${category} (${percentage.toFixed(1)}%)`}
                    >
                      {productData.revenueByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="product_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productData.revenueByCategory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#8884d8" name="Revenue (EGP)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Revenue by Product */}
          <div className="product_analysis_chart-container">
            <h3>Revenue by Product</h3>
            <div className="product_analysis_chart-grid">
              <div className="product_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={productData.revenueByProduct.slice(0, 10)}
                      dataKey="revenue"
                      nameKey="product"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ product, percentage }) => `${product.length > 15 ? product.substring(0, 15) + '...' : product} (${percentage.toFixed(1)}%)`}
                    >
                      {productData.revenueByProduct.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="product_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productData.revenueByProduct.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="product" tickFormatter={(value) => value.length > 10 ? value.substring(0, 10) + '...' : value} />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#8884d8" name="Revenue (EGP)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            {productData.revenueByProduct.length > 10 && (
              <div className="product_analysis_note">
                * Showing top 10 products by revenue. {productData.revenueByProduct.length - 10} more products not shown in the charts.
              </div>
            )}
          </div>
          
          {/* Revenue Over Time */}
          <div className="product_analysis_chart-container">
            <h3>Transactions Over Time</h3>
            
            {/* Transactions Line Chart */}
            <div className="product_analysis_chart">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={productData.revenueOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonth}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => value}
                    labelFormatter={formatMonth}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="transactions" 
                    stroke="#82ca9d" 
                    name="Number of Transactions" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="uniqueProducts" 
                    stroke="#8884d8" 
                    name="Unique Products" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Geographical Distribution */}
          <div className="product_analysis_chart-container">
            <div className="product_analysis_chart-header">
              <h3>Geographical Distribution</h3>
              <div className="product_analysis_tabs">
                <button 
                  className={`product_analysis_tab ${geographyView === 'region' ? 'active' : ''}`}
                  onClick={() => setGeographyView('region')}
                >
                  By Region
                </button>
                <button 
                  className={`product_analysis_tab ${geographyView === 'country' ? 'active' : ''}`}
                  onClick={() => setGeographyView('country')}
                >
                  By Country
                </button>
              </div>
            </div>
            
            <div className="product_analysis_chart-grid">
              <div className="product_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={geographyView === 'region' 
                        ? productData.productsByRegion 
                        : productData.productsByCountry.slice(0, 10)}
                      dataKey="revenue"
                      nameKey={geographyView === 'region' ? 'region' : 'country'}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percentage }) => `${name} (${percentage.toFixed(1)}%)`}
                    >
                      {(geographyView === 'region' 
                        ? productData.productsByRegion 
                        : productData.productsByCountry.slice(0, 10)).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="product_analysis_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={geographyView === 'region' 
                      ? productData.productsByRegion 
                      : productData.productsByCountry.slice(0, 10)}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={geographyView === 'region' ? 'region' : 'country'} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'revenue') return formatCurrency(value);
                        return value;
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenue (EGP)" />
                    <Bar yAxisId="right" dataKey="transactions" fill="#82ca9d" name="Transactions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Show note for country view if there are many countries */}
            {geographyView === 'country' && productData.productsByCountry.length > 10 && (
              <div className="product_analysis_note">
                * Showing top 10 countries by revenue. {productData.productsByCountry.length - 10} more countries not shown in the charts.
              </div>
            )}
          </div>
          
          {/* Product Performance Radar Chart */}
          <div className="product_analysis_chart-container">
            <h3>Product Performance Metrics</h3>
            <ResponsiveContainer width="100%" height={500}>
              <RadarChart outerRadius={150} data={productData.productPerformance.slice(0, 5)}>
                <PolarGrid />
                <PolarAngleAxis dataKey="name" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar name="Revenue %" dataKey="revenue" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Radar name="Transactions %" dataKey="transactions" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
            <div className="product_analysis_note">
              * Showing top 5 products by revenue. Values are shown as percentage of total.
            </div>
          </div>
          
          {/* Product Details Table */}
          <div className="product_analysis_table-container">
            <h3>Product Details</h3>
            <div className="product_analysis_table-wrapper">
              <table className="product_analysis_table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Revenue</th>
                    <th>Transactions</th>
                    <th>Customers</th>
                    <th>Avg. Revenue</th>
                    <th>Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredProducts().map((product, index) => (
                    <tr key={index} className={`product-category-${product.category}`}>
                      <td>{product.product}</td>
                      <td>{product.category}</td>
                      <td>{formatCurrency(product.revenue)}</td>
                      <td>{product.transactions}</td>
                      <td>{product.customers}</td>
                      <td>{formatCurrency(product.averageRevenue)}</td>
                      <td>{formatPercentage(product.percentage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductAnalysis;
